import { Check, Pencil, X } from 'lucide-react';
import { useId, useRef, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { IconButton } from './IconButton';
import { Input } from './Input';

export function PropertyList({ children }: { children: ReactNode }) {
  return (
    <dl data-slot="property-list" className="flex flex-col divide-y divide-line">
      {children}
    </dl>
  );
}

export function PropertyRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="grid min-h-10 grid-cols-[minmax(0,1fr)_minmax(0,2fr)] items-baseline gap-3 py-2">
      <dt className="min-w-0 text-xs break-words text-fg-muted">{label}</dt>
      <dd className="min-h-6 min-w-0 text-sm text-fg">{children}</dd>
    </div>
  );
}

/** Enter сохраняет, Escape отменяет только правку; панель остаётся открытой. */
export function EditableProperty({
  label,
  value,
  onCommit,
  validate,
}: {
  label: string;
  value: string;
  onCommit: (value: string) => void | Promise<void>;
  validate?: (value: string) => string | undefined;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const id = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const finish = () => {
    setEditing(false);
    requestAnimationFrame(() => buttonRef.current?.focus({ preventScroll: true }));
  };

  const save = async () => {
    const next = draft.trim();
    const problem = validate?.(next);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    try {
      await onCommit(next);
      finish();
    } catch {
      setError('Не удалось сохранить. Попробуй ещё раз.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PropertyRow label={<label htmlFor={editing ? id : undefined}>{label}</label>}>
      {editing ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              finish();
            }
          }}
          className="flex min-w-0 flex-col gap-2"
        >
          <Input
            id={id}
            aria-label={label}
            autoFocus
            value={draft}
            error={error}
            disabled={saving}
            onChange={(e) => {
              setDraft(e.target.value);
              setError(undefined);
            }}
          />
          {error && (
            <span role="alert" className="sr-only">
              {error}
            </span>
          )}
          <div className="flex items-center gap-1">
            <Button type="submit" variant="secondary" size="sm" disabled={saving}>
              <Check className="size-4" />
              {saving ? 'Сохраняю…' : 'Сохранить'}
            </Button>
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={`Отменить правку: ${label}`}
              disabled={saving}
              onClick={finish}
            >
              <X />
            </IconButton>
          </div>
        </form>
      ) : (
        <button
          type="button"
          data-slot="property-edit"
          ref={buttonRef}
          className="group/edit -mx-2 inline-flex min-h-6 max-w-full items-baseline gap-2 rounded-sm px-2 py-0.5 text-left text-sm transition-colors duration-fast hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          title={value}
          aria-label={`Изменить: ${label}`}
          onClick={() => {
            setDraft(value);
            setError(undefined);
            setEditing(true);
          }}
        >
          <span className="min-w-0 break-words whitespace-pre-wrap">{value || 'Не указано'}</span>
          <Pencil
            aria-hidden
            className="size-3.5 shrink-0 self-center opacity-0 transition-opacity duration-fast group-hover/edit:opacity-100 group-focus/edit:opacity-100"
          />
        </button>
      )}
    </PropertyRow>
  );
}
